import { APP_BUILD, BUILD_HEADER } from "./release";

/** One event of the page for the server log. Fields hold codes, counts and builds, never a customer value. */
export interface ClientEvent {
  name: string;
  at: string;
  build: string;
  path: string;
  fields: Record<string, string | number | boolean | null>;
}

type Field = string | number | boolean | null | undefined;
type TokenSource = () => Promise<string>;

export const LOG_KEY = "xflow-client-log";
export const LOG_MAX = 200;
export const BATCH_MAX = 50;
export const BATCH_BYTES = 15_000;
export const FLUSH_MS = 60_000;
export const FIRST_FLUSH_MS = 3_000;
const TEXT_MAX = 200;
const FIELDS_MAX = 20;
const NAME = /^[a-z][a-z0-9_]{0,39}$/;
const ORG_PATH = /^\/o\/[^/]+(\/[^/]+)?/;

interface Deps {
  storage: () => Pick<Storage, "getItem" | "setItem" | "removeItem"> | undefined;
  fetch: typeof fetch;
  now: () => number;
  console: Pick<Console, "info" | "warn">;
  path: () => string;
}

const DEFAULTS: Deps = {
  storage: () => localStorage,
  fetch: (...args) => fetch(...args),
  now: Date.now,
  console,
  path: () => (typeof location === "undefined" ? "" : location.pathname),
};
let deps: Deps = DEFAULTS;
let tokenSource: TokenSource | null = null;
let lastToken: string | null = null;
let off = false;
let sending = false;
let dropped = 0;
let memory: ClientEvent[] = [];
let timer: ReturnType<typeof setInterval> | null = null;
let first: ReturnType<typeof setTimeout> | null = null;

/** The org and the page of a path, without an id, so that the log holds no record. */
export function pagePath(path: string): string {
  return ORG_PATH.exec(path)?.[0] ?? path.split("/").slice(0, 2).join("/");
}

function clean(fields: Record<string, Field>): ClientEvent["fields"] {
  const out: ClientEvent["fields"] = {};
  for (const [key, value] of Object.entries(fields).slice(0, FIELDS_MAX)) {
    if (value === undefined || !NAME.test(key)) continue;
    out[key] = typeof value === "string" ? value.slice(0, TEXT_MAX) : typeof value === "number" && !Number.isFinite(value) ? null : value;
  }
  return out;
}

function readQueue(): ClientEvent[] {
  try {
    const kept = deps.storage()?.getItem(LOG_KEY);
    return kept ? (JSON.parse(kept) as ClientEvent[]) : memory;
  } catch {
    return memory;
  }
}

function writeQueue(queue: ClientEvent[]): void {
  memory = queue;
  try {
    const storage = deps.storage();
    if (!storage) return;
    if (queue.length) storage.setItem(LOG_KEY, JSON.stringify(queue));
    else storage.removeItem(LOG_KEY);
  } catch {
    // Without local storage, the queue stays in memory for this page only.
  }
}

/** Log one event in the console and queue it for the server. */
function event(name: string, fields: Record<string, Field> = {}): void {
  if (!NAME.test(name)) return deps.console.warn(`[client-log] bad event name ${name}`);
  const entry: ClientEvent = { name, at: new Date(deps.now()).toISOString(), build: APP_BUILD, path: pagePath(deps.path()), fields: clean(fields) };
  deps.console.info(`[client-log] ${name}`, entry.fields);
  if (off) return;
  const queue = [...readQueue(), entry];
  if (queue.length > LOG_MAX) {
    dropped += queue.length - LOG_MAX;
    queue.splice(0, queue.length - LOG_MAX);
  }
  writeQueue(queue);
}

/** The first events that fit in one request. */
export function batchOf(queue: ClientEvent[]): ClientEvent[] {
  const batch: ClientEvent[] = [];
  let bytes = 20;
  for (const entry of queue.slice(0, BATCH_MAX)) {
    bytes += JSON.stringify(entry).length + 1;
    if (bytes > BATCH_BYTES) break;
    batch.push(entry);
  }
  return batch;
}

/** The queue without the sent events. Each sent copy removes one equal copy. */
function without(queue: ClientEvent[], sent: ClientEvent[]): ClientEvent[] {
  const left = new Map<string, number>();
  for (const entry of sent) left.set(JSON.stringify(entry), (left.get(JSON.stringify(entry)) ?? 0) + 1);
  return queue.filter((entry) => {
    const key = JSON.stringify(entry);
    const count = left.get(key) ?? 0;
    if (count) left.set(key, count - 1);
    return count === 0;
  });
}

function body(batch: ClientEvent[]): string {
  const sent = dropped ? [...batch, { name: "log_dropped", at: new Date(deps.now()).toISOString(), build: APP_BUILD, path: "", fields: { count: dropped } }] : batch;
  return JSON.stringify({ events: sent });
}

/** Send the queue. `leaving`: the page closes, so it uses the kept token and does not wait. */
async function flush(leaving = false): Promise<void> {
  if (off || sending || (!tokenSource && !lastToken)) return;
  const batch = batchOf(readQueue());
  if (!batch.length) return;
  sending = true;
  try {
    if (!leaving && tokenSource) lastToken = await tokenSource().catch(() => lastToken);
    if (!lastToken) return;
    const response = await deps.fetch("/api/client/events", {
      method: "POST",
      keepalive: true,
      headers: { Authorization: `Bearer ${lastToken}`, "Content-Type": "application/json", [BUILD_HEADER]: APP_BUILD },
      body: body(batch),
    });
    if (response.status === 404) {
      off = true;
      writeQueue([]);
      deps.console.info("[client-log] the API has no client log yet; the events stay in the console only");
      return;
    }
    if (!response.ok) return deps.console.info(`[client-log] the server answered ${response.status}; ${batch.length} events wait`);
    dropped = 0;
    writeQueue(without(readQueue(), batch));
  } catch {
    // A network error keeps the queue for the next flush.
  } finally {
    sending = false;
  }
}

/** Give the log the token of the signed-in user; null at sign-out. */
function signIn(source: TokenSource | null): () => void {
  tokenSource = source;
  if (!source) {
    lastToken = null;
    return () => undefined;
  }
  if (first) clearTimeout(first);
  first = setTimeout(() => void flush(), FIRST_FLUSH_MS);
  return () => {
    if (tokenSource === source) signIn(null);
  };
}

/** Flush each minute and when the page closes. */
function start(): void {
  if (timer) return;
  timer = setInterval(() => void flush(), FLUSH_MS);
  addEventListener("pagehide", () => void flush(true));
}

export const clientLog = { event, flush, signIn, start, pending: () => readQueue().length };

/** For tests. */
export function resetClientLog(next: Partial<Deps> = {}): void {
  deps = { ...DEFAULTS, ...next };
  tokenSource = null;
  lastToken = null;
  off = false;
  sending = false;
  dropped = 0;
  memory = [];
  if (timer) clearInterval(timer);
  if (first) clearTimeout(first);
  timer = null;
  first = null;
}
